import { useTabPanel } from '@mui/base';
import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { TabPanelProps } from './tab-panel.types';

import './tab-panel.scss';

/**
 * The card that hosts the content associated with a tab.
 *
 * ### Usages
 *
 * ```tsx
 * import { Tabs, TabList, Tab, TabPanel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Tabs>
 *       <TabList>
 *         <Tab value="tab-1">Tab 1</Tab>
 *         <Tab value="tab-2">Tab 2</Tab>
 *         <Tab value="tab-3">Tab 3</Tab>
 *       </TabList>
 *       <TabPanel value="tab-1" keepMounted>Tab 1 content</TabPanel>
 *       <TabPanel value="tab-2">Tab 2 content</TabPanel>
 *       <TabPanel value="tab-3">Tab 3 content</TabPanel>
 *     </Tabs>
 *   );
 * }
 * ```
 */
const TabPanel = forwardRef((props: TabPanelProps, forwardedRef: ForwardedRef<HTMLElement> | null): ReactElement => {
  const {
    children,
    className,
    keepMounted = false,
    value: valueProp,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const { getRootProps, hidden, rootRef } = useTabPanel({
    rootRef: forwardedRef,
    value: valueProp,
  });

  const rootProps = getRootProps({ ...rest, role: 'tabpanel' });

  const classes = cn(
    'rbui-tab-panel',
    {
      'rbui-tab-panel--hidden': hidden,
    },
    className,
  );

  return (
    <div {...rootProps} className={classes} ref={rootRef as React.RefObject<HTMLDivElement>}>
      {(keepMounted || !hidden) && children}
    </div>
  );
});

TabPanel.displayName = 'TabPanel';

export default TabPanel;
